import {
	type Environment,
	loadQuery,
	type LoadQueryOptions,
	type PreloadedQuery,
	type VariablesOf,
} from 'react-relay';
import {
	createResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
	type RouteResource,
	useResource,
} from 'react-resource-router-next';
import type { ConcreteRequest, OperationType } from 'relay-runtime';

import RelayEnvironment from './RelayEnvironment';

export interface RelayResourceConfig<TQuery extends OperationType> {
	cacheKey?: string;
	environment?: Environment;
	options?: LoadQueryOptions;
	query: ConcreteRequest;
	variables?: VariablesOf<TQuery>;
}

function createCacheKey<TQuery extends OperationType>(
	query: ConcreteRequest,
	variables?: VariablesOf<TQuery>,
) {
	const queryId = 'cacheID' in query.params ? query.params.cacheID : query.params.id;
	return `relay-${queryId}-${query.params.name}-${JSON.stringify(variables ?? {})}`;
}

/**
 * @deprecated please use entrypoint instead https://hello.atlassian.net/wiki/spaces/agents/pages/5596152473/RFC-003+Nested+entrypoints+architecture
 */
export const createRelayResource = <TQuery extends OperationType>({
	getQuery,
	params,
}: {
	getQuery: (
		routerContext: RouterContext | RouterDataContext,
		resourceStoreContext: ResourceStoreContext,
	) => RelayResourceConfig<TQuery>;
	params?: Pick<RouteResource, 'maxAge'>;
}): RouteResource<PreloadedQuery<TQuery, Record<string, unknown>>> =>
	createResource<PreloadedQuery<TQuery>>({
		type: 'RELAY_RESOURCE_TYPE',
		getKey: (routerContext: RouterContext, customContext: ResourceStoreContext) => {
			const { query, variables, cacheKey } = getQuery(routerContext, customContext);
			return cacheKey ?? createCacheKey(query, variables);
		},
		getData: (routerContext: RouterDataContext, customContext: ResourceStoreContext) => {
			const { query, variables, options } = getQuery(routerContext, customContext);

			// @ts-expect-error this is expected because ResourceStoreContext is interface to identify how context is injected, but unfortunately the type is hardcoded to {}
			const customRelayEnvironment = customContext?.relayEnvironment;
			const queryReference = loadQuery<TQuery>(
				customRelayEnvironment ?? RelayEnvironment,
				query,
				variables ?? {},
				options,
			);
			return Promise.resolve(queryReference);
		},
		...params,
	});

/**
 * @deprecated please use entrypoint instead https://hello.atlassian.net/wiki/spaces/agents/pages/5596152473/RFC-003+Nested+entrypoints+architecture
 */
export const useRelayResource = <Query extends OperationType>(
	resource: RouteResource<PreloadedQuery<Query>>,
): {
        queryReference: PreloadedQuery<Query, Record<string, unknown>> | null;
    } => {
	const { data: queryReference } = useResource(resource);
	return {
		queryReference,
	};
};
